import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PURGE } from 'redux-persist'

import type { ActivityPriority, CrmAccount, CrmAccountFilters } from 'features/crmAccounts/types'
import type { RootState } from 'store/store'

export type CrmDetailTab = 'contacts' | 'activities' | 'referrals'

/**
 * Per-tab filter for the Account Detail tabs (US 15839 / 15840). Held as a
 * SINGLE array (one entry per tab) so all three tabs share one Redux shape.
 * Referrals is reserved (out of current scope) but kept in the array.
 */
export interface CrmTabFilter {
  tab: CrmDetailTab
  roleIds: number[]
  engagementTypeIds: number[]
  priorities: ActivityPriority[]
  // Referrals tab (US 15841) — persisted so Status/Community filters survive tab
  // switches and leaving/returning within the session. (16810)
  statusIds: number[]
  communityIds: number[]
}

export interface CrmAccountState {
  searchQuery: string
  filters: CrmAccountFilters
  page: number
  pageSize: number
  sortValue: string
  /** SA-only org selector value; null for org/community users (scoped via JWT) */
  selectedOrgId: number | null
  isAddAccountOpen: boolean
  editingAccountId: string | null
  currentAccount: CrmAccount | null
  activeDetailTab: CrmDetailTab
  tabFilters: CrmTabFilter[]
}

const initialFilters: CrmAccountFilters = {
  organizationId: null,
  communityIds: [],
  types: [],
  status: null,
}

const emptyTabFilter = (tab: CrmDetailTab): CrmTabFilter => ({
  tab,
  roleIds: [],
  engagementTypeIds: [],
  priorities: [],
  statusIds: [],
  communityIds: [],
})

const ALL_DETAIL_TABS: CrmDetailTab[] = ['contacts', 'activities', 'referrals']

const makeInitialTabFilters = (): CrmTabFilter[] => ALL_DETAIL_TABS.map(emptyTabFilter)

const initialTabFilters: CrmTabFilter[] = makeInitialTabFilters()

const initialState: CrmAccountState = {
  searchQuery: '',
  filters: initialFilters,
  page: 1,
  pageSize: 10,
  sortValue: 'name',
  selectedOrgId: null,
  isAddAccountOpen: false,
  editingAccountId: null,
  currentAccount: null,
  activeDetailTab: 'contacts',
  tabFilters: initialTabFilters,
}

export const crmAccountSlice = createSlice({
  name: 'crmAccount',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
      state.page = 1
    },
    setFilters: (state, action: PayloadAction<CrmAccountFilters>) => {
      state.filters = action.payload
      state.page = 1
    },
    clearFilters: state => {
      state.filters = initialFilters
      state.page = 1
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload
      state.page = 1
    },
    setSortValue: (state, action: PayloadAction<string>) => {
      state.sortValue = action.payload
      state.page = 1
    },
    setSelectedOrgId: (state, action: PayloadAction<number | null>) => {
      const orgChanged = state.selectedOrgId !== action.payload
      state.selectedOrgId = action.payload
      if (orgChanged) {
        state.page = 1
        state.sortValue = initialState.sortValue
        state.searchQuery = ''
        state.filters = initialFilters
      }
    },
    openAddAccount: state => {
      state.isAddAccountOpen = true
      state.editingAccountId = null
    },
    openEditAccount: (state, action: PayloadAction<string>) => {
      state.isAddAccountOpen = true
      state.editingAccountId = action.payload
    },
    closeAccountModal: state => {
      state.isAddAccountOpen = false
      state.editingAccountId = null
    },
    setCurrentAccount: (state, action: PayloadAction<CrmAccount | null>) => {
      state.currentAccount = action.payload
    },
    setActiveDetailTab: (state, action: PayloadAction<CrmDetailTab>) => {
      state.activeDetailTab = action.payload
    },
    setTabFilter: (
      state,
      action: PayloadAction<{ tab: CrmDetailTab; patch: Partial<Omit<CrmTabFilter, 'tab'>> }>,
    ) => {
      // Guard against pre-tabFilters persisted state rehydrated without the field.
      if (!state.tabFilters) state.tabFilters = makeInitialTabFilters()
      const entry = state.tabFilters.find(f => f.tab === action.payload.tab)
      if (entry) Object.assign(entry, action.payload.patch)
    },
    clearTabFilter: (state, action: PayloadAction<CrmDetailTab>) => {
      if (!state.tabFilters) state.tabFilters = makeInitialTabFilters()
      const idx = state.tabFilters.findIndex(f => f.tab === action.payload)
      if (idx !== -1) state.tabFilters[idx] = emptyTabFilter(action.payload)
    },
    resetListing: () => initialState,
  },
  extraReducers: builder => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const crmAccountActions = crmAccountSlice.actions

export const selectCrmSearchQuery = (state: RootState) => state.crmAccount.searchQuery
const selectRawCrmFilters = (state: RootState) => state.crmAccount.filters
export const selectCrmFilters = createSelector(
  [selectRawCrmFilters],
  (filters): CrmAccountFilters => ({ ...initialFilters, ...filters }),
)
export const selectCrmPage = (state: RootState) => state.crmAccount.page
export const selectCrmPageSize = (state: RootState) => state.crmAccount.pageSize
export const selectCrmSortValue = (state: RootState) =>
  state.crmAccount.sortValue ?? 'name'
export const selectCrmSelectedOrgId = (state: RootState) => state.crmAccount.selectedOrgId
export const selectIsAddAccountOpen = (state: RootState) =>
  state.crmAccount.isAddAccountOpen
export const selectEditingAccountId = (state: RootState) =>
  state.crmAccount.editingAccountId
export const selectCurrentAccount = (state: RootState) => state.crmAccount.currentAccount
export const selectActiveDetailTab = (state: RootState) =>
  state.crmAccount.activeDetailTab
const selectRawCrmTabFilters = (state: RootState) => state.crmAccount.tabFilters
/**
 * Normalises the persisted tab filters so reads never see `undefined` (stale
 * state rehydrated before the field existed) or a partial array. Memoised so the
 * reference stays stable across renders — consumers depend on it in useMemo.
 */
export const selectCrmTabFilters = createSelector(
  [selectRawCrmTabFilters],
  (tabFilters): CrmTabFilter[] => {
    if (!tabFilters || tabFilters.length === 0) return initialTabFilters
    // Merge over emptyTabFilter so entries rehydrated before newer fields existed
    // (e.g. statusIds/communityIds) never read as undefined. (16810)
    return ALL_DETAIL_TABS.map(tab => {
      const found = tabFilters.find(f => f.tab === tab)
      return found ? { ...emptyTabFilter(tab), ...found } : emptyTabFilter(tab)
    })
  },
)

export default crmAccountSlice.reducer
